import React, { useState } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import sample data
import { countries, regions } from '../../data/geoData';
import { productCategoriesL1 } from '../../data/productData';
import { monthlySalesByRegion } from '../../data/timeData';

const GeoOverview = () => {
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedProductCategory, setSelectedProductCategory] = useState('all');
  
  // Filter countries based on selected region
  const getFilteredCountries = () => {
    if (selectedRegion === 'all') {
      return countries;
    } else {
      const regionName = regions.find(r => r.id === parseInt(selectedRegion))?.name;
      return countries.filter(country => country.region === regionName);
    }
  };
  
  // Prepare data for regional sales chart
  const getRegionalSalesData = () => {
    return regions.map(region => ({
      name: region.name,
      sales: region.totalSales,
      growth: region.growth
    }));
  };
  
  // Get top countries by sales
  const getTopCountriesBySales = () => {
    return [...getFilteredCountries()]
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 10);
  };
  
  // Prepare data for pie chart
  const preparePieData = () => {
    const topCountries = getTopCountriesBySales().slice(0, 5);
    const otherCountries = getFilteredCountries()
      .filter(country => !topCountries.some(c => c.id === country.id));
    
    const result = topCountries.map(country => ({
      name: country.name,
      value: country.totalSales
    }));
    
    if (otherCountries.length > 0) {
      const otherSales = otherCountries.reduce((sum, country) => sum + country.totalSales, 0);
      result.push({
        name: 'Other Countries',
        value: otherSales
      });
    }
    
    return result;
  };
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1'];
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="content-card">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Geographic Performance Overview</h1>
        <p className="text-gray-600 mb-4">
          Analyze sales performance across different geographical regions. View regional distribution and country comparisons.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Region</label>
            <select 
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="dropdown-selector"
            >
              <option value="all">All Regions</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>{region.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Product Category</label>
            <select 
              value={selectedProductCategory}
              onChange={(e) => setSelectedProductCategory(e.target.value)}
              className="dropdown-selector"
            >
              <option value="all">All Categories</option>
              {productCategoriesL1.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Regional Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="chart-container">
          <h2 className="chart-title">Regional Sales Distribution</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={preparePieData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {preparePieData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="chart-container">
          <h2 className="chart-title">Regional Performance</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={getRegionalSalesData()}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
                <Legend />
                <Bar dataKey="sales" fill="#0088FE" name="Total Sales" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Country comparison table */}
      <div className="content-card">
        <h2 className="section-title">Country Performance</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Region</th>
                <th>Total Sales</th>
                <th>Growth</th>
                <th>Shop Count</th>
                <th>Area (km²)</th>
              </tr>
            </thead>
            <tbody>
              {getTopCountriesBySales().map(country => (
                <tr key={country.id}>
                  <td>{country.name}</td>
                  <td>{country.region}</td>
                  <td>${(country.totalSales / 1000000).toFixed(2)}M</td>
                  <td className={country.growth >= 0 ? 'positive-growth' : 'negative-growth'}>
                    {country.growth >= 0 ? '↑' : '↓'} {Math.abs(country.growth).toFixed(1)}%
                  </td>
                  <td>{country.shopCount}</td>
                  <td>{country.area_km2?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Regional sales trend */}
      <div className="chart-container">
        <h2 className="chart-title">Regional Sales Trends (Monthly)</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={monthlySalesByRegion}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
              <Legend />
              {regions.map((region, index) => (
                <Bar 
                  key={region.id} 
                  dataKey={region.name} 
                  stackId="a" 
                  fill={COLORS[index % COLORS.length]} 
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default GeoOverview;